import {announce} from '@github-ui/aria-live'
import {testIdProps} from '@github-ui/test-id-props'
import {useLabelStyles} from '@github-ui/use-label-styles'
import {SyncIcon} from '@primer/octicons-react'
import {Button, FormControl, IconButton, TextInput} from '@primer/react'
import {useEffect, useId, useRef, useState} from 'react'

import styles from './LabelColorPicker.module.css'

const DARK_COLORS = ['#b60205', '#d93f0b', '#fbca04', '#0e8a16', '#006b75', '#1d76db', '#0052cc', '#5319e7']
const LIGHT_COLORS = ['#e99695', '#f9d0c4', '#fef2c0', '#c2e0c6', '#bfdadc', '#c5def5', '#bfd4f2', '#d4c5f9']

const COLOR_NAMES: Record<string, string> = {
  '#b60205': 'Red',
  '#d93f0b': 'Orange',
  '#fbca04': 'Yellow',
  '#0e8a16': 'Green',
  '#006b75': 'Teal',
  '#1d76db': 'Blue',
  '#0052cc': 'Navy',
  '#5319e7': 'Purple',
  '#e99695': 'Pink',
  '#f9d0c4': 'Light Pink',
  '#fef2c0': 'Light Yellow',
  '#c2e0c6': 'Light Green',
  '#bfdadc': 'Light Teal',
  '#c5def5': 'Light Blue',
  '#bfd4f2': 'Light Indigo',
  '#d4c5f9': 'Lavender',
}

type LabelColorPickerProps = {
  color: string
  onChangeCallback?: (color: string) => void
  disabled?: boolean
  required?: boolean
  validationError?: string | null
  fullWidth?: boolean
}

export function randomHexColor(): string {
  const randomChannel = () => Math.floor(Math.random() * 256)
  return `#${[randomChannel(), randomChannel(), randomChannel()].map(c => c.toString(16).padStart(2, '0')).join('')}`
}

export function isValidColor(color: string): boolean {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)
}

export function normalizeHexColor(color: string): string {
  const shorthand = /^#([0-9A-Fa-f]{3})$/.exec(color)?.[1]
  return shorthand ? `#${[...shorthand].map(character => character.repeat(2)).join('')}` : color
}

export function getReadableForegroundColor(color: string): '#000' | '#fff' {
  const normalizedColor = normalizeHexColor(color)
  const toLinearChannel = (channel: number) => {
    const value = channel / 255
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }
  const red = toLinearChannel(Number.parseInt(normalizedColor.slice(1, 3), 16))
  const green = toLinearChannel(Number.parseInt(normalizedColor.slice(3, 5), 16))
  const blue = toLinearChannel(Number.parseInt(normalizedColor.slice(5, 7), 16))
  const luminance = red * 0.2126 + green * 0.7152 + blue * 0.0722

  return luminance > 0.179 ? '#000' : '#fff'
}

export const LabelColorPicker = ({
  ref: inputRef,
  color,
  onChangeCallback,
  disabled = false,
  required = false,
  validationError,
  fullWidth = false,
}: LabelColorPickerProps & {ref?: React.Ref<HTMLInputElement>}) => {
  const [currentInputValue, setCurrentInputValue] = useState(color)
  const [currentColor, setCurrentColor] = useState(color)
  const [showPopup, setShowPopup] = useState(false)
  const popupId = useId()
  const labelStyles = useLabelStyles(1, currentColor)
  const hasShownValidationErrorRef = useRef(Boolean(validationError))

  useEffect(() => {
    if (!validationError) return

    if (!hasShownValidationErrorRef.current) {
      hasShownValidationErrorRef.current = true
      return
    }

    const timeoutId = window.setTimeout(() => announce(validationError), 300)
    return () => window.clearTimeout(timeoutId)
  }, [validationError])

  const handlePickerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // Close the popup once focus leaves the input and popup entirely, so the
    // floating popup can't obscure controls that follow it in the tab order
    // (WCAG 2.4.11 Focus Not Obscured).
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setShowPopup(false)
    }
  }

  const handleColorChange = (newColor: string) => {
    if (disabled) return

    setCurrentInputValue(newColor)

    // Validate the color input
    if (isValidColor(newColor)) {
      setCurrentColor(newColor)
    }

    onChangeCallback?.(newColor)
  }

  const handleRandomColor = () => handleColorChange(randomHexColor())

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => handleColorChange(e.target.value)

  const handleColorSelect = (selectedColor: string) => {
    if (disabled) return

    handleColorChange(selectedColor)
    setShowPopup(false)
  }

  const colorButton = (c: string, i: number) => (
    <Button
      variant="invisible"
      key={i}
      className={styles.color}
      style={{backgroundColor: c}}
      aria-label={`${COLOR_NAMES[c] ?? 'Color:'} (${c})`}
      onClick={() => {
        handleColorSelect(c)
      }}
      aria-disabled={disabled || undefined}
      tabIndex={0}
    />
  )

  return (
    <FormControl required={required}>
      <FormControl.Label requiredText={required ? ' (required)' : undefined}>Color</FormControl.Label>
      <div className={styles.container} data-full-width={fullWidth || undefined}>
        <IconButton
          style={{
            ...labelStyles,
            backgroundColor: currentColor,
            color: getReadableForegroundColor(currentColor),
          }}
          icon={SyncIcon}
          aria-label="Choose random color"
          title="Choose random color"
          onClick={handleRandomColor}
          aria-disabled={disabled || undefined}
        />
        <div className={styles.inputContainer} onBlur={handlePickerBlur}>
          <FormControl required={required}>
            <FormControl.Label visuallyHidden>
              {`${COLOR_NAMES[currentColor] ?? 'Color:'} (${currentColor})`}
            </FormControl.Label>
            <TextInput
              block={fullWidth}
              {...testIdProps('color-input')}
              value={currentInputValue}
              onChange={handleInputChange}
              onFocus={() => {
                if (!disabled) setShowPopup(true)
              }}
              readOnly={disabled}
              aria-disabled={disabled || undefined}
              validationStatus={isValidColor(currentInputValue) ? 'success' : 'error'}
              ref={inputRef}
              role="combobox"
              aria-haspopup="dialog"
              aria-expanded={showPopup}
              aria-controls={showPopup ? popupId : undefined}
            />
            {validationError ? (
              <FormControl.Validation variant="error">{validationError}</FormControl.Validation>
            ) : null}
          </FormControl>
          {showPopup && (
            <div
              id={popupId}
              role="dialog"
              aria-label="Choose from default colors"
              className={styles.popup}
              tabIndex={-1} // Make the popup focusable to allow `relatedTarget` to work
            >
              <span className={styles.text}>Choose from default colors</span>
              <div className={styles.colors}>{DARK_COLORS.map(colorButton)}</div>
              <div className={styles.colors}>{LIGHT_COLORS.map(colorButton)}</div>
            </div>
          )}
        </div>
      </div>
    </FormControl>
  )
}

LabelColorPicker.displayName = 'LabelColorPicker'
